import { inject, Injectable } from '@angular/core';
import { IAccount, IAccountPreferences } from '@okcargo-apps/shared-domain-ionic';
import {
  CollaboratorRoutesPreferencesFieldNames,
  FilterCombinator,
  FilterComparator,
  FindCollaboratorQuestionairQuery,
  FindContactQuery,
  OrderType,
  SearchCollaboratorRoutesPreferencesQuery,
} from '@okcargo/query-processor';
import { CompanyQueryService } from '../common/company-query.service';
import { ContactQueryService } from '../common/contact-query.service';
import { FreightQueryService } from '../common/freight-query.service';
import { AccountDataQueryService } from './account-data-query.service';
import { transformAccountResponseToIAccount, transformToIAccountPreferences } from './account-mapper';

@Injectable({
  providedIn: 'root',
})
export class AccountQueryService {
  private readonly freightQueryService = inject(FreightQueryService);
  private readonly accountDataQueryService = inject(AccountDataQueryService);
  private readonly companyQueryService = inject(CompanyQueryService);
  private readonly contactQueryService = inject(ContactQueryService);

  public async getIdentity(
    account: IAccount,
    accountPreferences: IAccountPreferences,
  ): Promise<{ account: IAccount; accountPreferences: IAccountPreferences }> {
    // parallel execution with Promise.all
    const [accountResponse, companyResponse, sitesResponse] = await Promise.all([
      this.accountDataQueryService.findAccount(),
      this.companyQueryService.findCompany(),
      this.companyQueryService.findSites(),
    ]);
    return transformAccountResponseToIAccount(
      accountResponse,
      companyResponse,
      sitesResponse,
      account,
      accountPreferences,
    );
  }

  public async findAccountPreferences(
    companyId: any,
    userEmail: string,
    accountPreferences: IAccountPreferences,
  ): Promise<IAccountPreferences> {
    const findCollaboratorQuestionairQuery = {
      carrierId: companyId,
    } as FindCollaboratorQuestionairQuery;

    const searchCollaboratorRoutesPreferencesQuery = {
      criteriaPayload: {
        filters: [
          {
            field: CollaboratorRoutesPreferencesFieldNames.CARRIER_ID,
            comparator: FilterComparator.EQ,
            value: companyId,
            combinator: FilterCombinator.NONE,
            group: null,
          },
        ],
        order: [
          {
            field: CollaboratorRoutesPreferencesFieldNames.ID,
            type: OrderType.ASC,
          },
        ],
        pageIndex: 0,
        pageSize: 1000,
      },
    } as unknown as SearchCollaboratorRoutesPreferencesQuery;

    const [accountPreferencesBaseResponse, accountPreferencesRoutesResponse, companyResponse, contactResponse] =
      await Promise.all([
        this.freightQueryService.findCollaboratorQuestionair(findCollaboratorQuestionairQuery),
        this.freightQueryService.searchCollaboratorRoutesPreferences(searchCollaboratorRoutesPreferencesQuery),
        this.companyQueryService.findCompany(),
        this.contactQueryService.findContact({} as FindContactQuery),
      ]);

    transformToIAccountPreferences(
      accountPreferencesBaseResponse,
      accountPreferencesRoutesResponse,
      companyResponse,
      contactResponse,
      accountPreferences,
      userEmail,
    );

    return accountPreferences;
  }
}
